import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-10 md:px-6 md:py-14">
        <div className="mb-8">
          <Link to="/" className="text-sm font-semibold text-blue-700 hover:text-blue-800">
            Voltar para a home
          </Link>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Política de Privacidade - Sinal Invest
          </h1>
          <p className="text-sm text-gray-500 mb-8">
            Última atualização: 04 de março de 2026
          </p>

          <div className="space-y-6 text-gray-700 leading-relaxed">
            <section>
              <p>
                A Sinal Invest ("nós", "nosso" ou "plataforma") valoriza a sua privacidade. Esta Política de
                Privacidade explica como coletamos, usamos, compartilhamos e protegemos as informações pessoais
                dos usuários ("você") ao utilizarem nossa plataforma web e nosso bot no Telegram.
              </p>
              <p className="mt-3">
                Ao acessar e utilizar a Sinal Invest, você concorda com as práticas descritas neste documento.
                Caso não concorde, recomendamos que não utilize nossos serviços.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">1. Dados que Coletamos</h2>
              <p className="mb-2">
                Para fornecer nossos serviços de análise de mercado e alertas, coletamos os seguintes tipos de
                informações:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Dados de Cadastro:</strong> Nome, endereço de e-mail e senha (criptografada)
                  fornecidos ao criar sua conta.
                </li>
                <li>
                  <strong>Dados de Integração:</strong> ID de usuário do Telegram, nome de usuário e histórico
                  de interações com nosso bot oficial, essenciais para o envio dos alertas de mercado.
                </li>
                <li>
                  <strong>Dados Contratuais:</strong> Informações cadastrais do escritório contratante
                  (razão social, CNPJ, responsável legal) utilizadas exclusivamente para formalização do
                  contrato e emissão de notas fiscais. Não realizamos cobrança direta por cartão de crédito
                  na plataforma.
                </li>
                <li>
                  <strong>Dados de Navegação e Uso:</strong> Endereço IP, tipo de navegador, páginas acessadas
                  no dashboard e configurações de filtros de empresas da B3. Utilizamos cookies essenciais para
                  manter sua sessão ativa e segura.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">2. Como Usamos Seus Dados</h2>
              <p className="mb-2">A Sinal Invest utiliza as informações coletadas estritamente para:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Autenticar seu acesso ao dashboard web.</li>
                <li>
                  Entregar as análises e alertas de notícias do mercado financeiro via Telegram ou e-mail,
                  conforme suas preferências de empresas e tickers.
                </li>
                <li>Gerenciar o contrato firmado com o escritório contratante e emitir documentação fiscal.</li>
                <li>Melhorar nossos algoritmos de recomendação e a usabilidade da plataforma.</li>
                <li>Prevenir fraudes e garantir a segurança da infraestrutura.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                3. Compartilhamento de Dados com Terceiros
              </h2>
              <p className="mb-2">
                A natureza tecnológica do nosso serviço exige que operemos em conjunto com parceiros de
                infraestrutura. Seus dados podem ser processados pelas seguintes categorias de serviços:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Provedores de Nuvem e Banco de Dados:</strong> Nossos servidores e bancos de dados
                  são hospedados em infraestrutura de nuvem segura (como a AWS - Amazon Web Services).
                </li>
                <li>
                  <strong>APIs de Inteligência Artificial:</strong> Utilizamos modelos de linguagem (como os da
                  OpenAI) para gerar os resumos e análises de impacto das notícias. <strong>Nota:</strong> Apenas
                  os textos das notícias públicas são enviados para a IA; nenhum dado pessoal identificável do
                  usuário é enviado para treinamento desses modelos.
                </li>
                <li>
                  <strong>Plataformas de Mensageria:</strong> O Telegram atua como canal de entrega, processando
                  seu ID para rotear nossas mensagens.
                </li>
              </ul>
              <p className="mt-3">
                Não vendemos, alugamos ou repassamos seus dados pessoais para terceiros para fins de marketing.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">4. Armazenamento e Segurança</h2>
              <p>
                Implementamos medidas técnicas e organizacionais padrão da indústria para proteger seus dados
                contra acesso não autorizado, alteração ou destruição. Senhas são armazenadas com hash
                criptográfico, e a comunicação entre seu navegador e nossos servidores ocorre via protocolo
                seguro (HTTPS/SSL).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">5. Seus Direitos (LGPD)</h2>
              <p className="mb-2">
                Em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018), você tem o direito de:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Solicitar a confirmação da existência de tratamento de dados.</li>
                <li>Acessar os dados que possuímos sobre você.</li>
                <li>Corrigir dados incompletos, inexatos ou desatualizados.</li>
                <li>
                  Solicitar a exclusão da sua conta e anonimização/deleção dos seus dados pessoais dos nossos
                  servidores (exceto quando a retenção for exigida por lei, como registros fiscais de contratos).
                </li>
                <li>Revogar seu consentimento para comunicações futuras.</li>
              </ul>
              <p className="mt-3">
                Para exercer seus direitos, basta desvincular o bot no Telegram e/ou solicitar a exclusão da conta
                no próprio painel de configurações, ou entrar em contato pelos canais oficiais.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">6. Contato</h2>
              <p>
                Se você tiver dúvidas sobre esta Política de Privacidade ou sobre o tratamento dos seus dados,
                entre em contato com nosso Encarregado de Proteção de Dados (DPO) através do e-mail:{' '}
                <a href="mailto:suporte@sinalinvest.com.br" className="text-blue-700 underline">
                  contato@sinalinvest.com
                </a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;

